import _StackFrame      from 'stackframe';
import ErrorStackParser from 'error-stack-parser';
import StacktraceGPS    from 'stacktrace-gps';

export class StackFrame extends _StackFrame {
	constructor(options : _StackFrame.StackFrameOptions) {
		super(options);
	};
};
const gps = new StacktraceGPS;

export class StackFrames {
	static async fromError(error : Error) {
		const frames = await Promise.all(
			ErrorStackParser.parse(error).
			map(async (frame) => {
				try {
					return (await gps.pinpoint(frame));
				} catch (e) {
				}
				return (frame);
			})
		);
		const sf = new StackFrames;
		sf.frames.push(...frames);
		return (sf);
	};

	static async fromStack(stack : string) {
		return (await this.fromError(<Error> {
			stack
		}));
	};

	static async fromPosition() {
		const stackFrames = await this.fromError(new Error);
		stackFrames.frames = stackFrames.frames.slice(2);
		return (stackFrames);
	};

	frames : StackFrame[] = [];
};

export interface IErrorDetails {
	[ key : string ] : unknown;

	readonly isExtendedError : true;
	readonly boundError? : IErrorExtended | Error; // Error who caused this error
	readonly boundEvent? : Event; // Event who caused this error
	readonly bound? : unknown; // Something undetermined who caused this error
	readonly isUserError : boolean;
	readonly isLibraryError : boolean;
	/** @deprecated **/
	readonly boundTo? : unknown;
};

export interface IErrorExtended extends Error {
	readonly details : IErrorDetails;
};

export interface IErrorExtendedPartial extends Error {
	details? : Partial<IErrorDetails>;
};
const all : IErrorExtended[] = [];
let last : IErrorExtended | null = null;
const createError = (
	Error : ErrorConstructor,
	msg : string,
	data0 : Partial<IErrorDetails> | null,
	data1 : {
		isUserError : boolean;
		isLibraryError : boolean;
	}
) => {
	const error : IErrorExtended = Object.assign(new Error(msg), {
		details : {
			...data0,
			...data1,
			isExtendedError : <true> true
		}
	});
	addError(error);
	return (error);
};
const createInfo = async (error : Error | IErrorExtended) => {
	var out : {
		name : string;
		message : string;
		type : string;
		stacktrace : StackFrames['frames'];
		details? : IErrorDetails;
	} = {
		name :       error.name,
		message :    error.message,
		type :       'native',
		stacktrace : (await StackFrames.fromError(error)).frames
	};
	if (isExtendedError(error)) {
		const details : Partial<IErrorDetails> = {};
		for (const [ key, value ] of Object.entries(error.details)) {
			if (key === 'isLibraryError') {
				if (value) {
					out.type = 'library';
				}
			} else if (key === 'isUserError') {
				if (value) {
					out.type = 'user';
				}
			} else if (key !== 'boundTo') {
				details[ key ] = value;
			}
		}
		if (Object.keys(details).length > 0) {
			out.details = <IErrorDetails> details;
		}
	}
	return out;
};
const onErrorEvent = (event : ErrorEvent) => {
	if ('error' in event) {
		if (event.error instanceof Error) {
			addError(library('Unhandled error!', {
				error : event.error
			}));
		} else if (event.error instanceof Event) {
			addError(library('Unhandled error!', {
				boundEvent : event.error
			}));
		} else {
			addError(library('Unhandled error!', {
				bound : event.error
			}));
		}
	} else {
		addError(library('Unhandled error!', {
			boundEvent : event
		}));
	}
};
if (globalThis.window) { // make compatible with Node.js
	window.addEventListener('error', onErrorEvent);
}

export function user(
	msg : string,
	data? : Partial<IErrorDetails>
)
{
	return (createError(Error, msg, data || null, {
		isUserError :    true,
		isLibraryError : false
	}));
};

export function library(
	msg : string,
	data? : Partial<IErrorDetails>
)
{
	return (createError(Error, msg, data || null, {
		isUserError :    false,
		isLibraryError : true
	}));
};

export function bind(
	msg : string,
	data : Partial<IErrorDetails>,
	boundError : Error | IErrorExtended | Event
)
{
	const boundErrorIsAnError = boundError instanceof Error;
	userAssert(
		boundErrorIsAnError || (boundError instanceof Event),
		'Bound error is not an valid binding!',
		{
			bound : boundError
		}
	);
	const category = (
		(
			data.isUserError ||
			(boundErrorIsAnError && isExtendedError(boundError) && boundError.details.isUserError)
		) ? 'user' : 'library'
	);
	const details : Partial<IErrorDetails> = Object.assign(
		{},
		data,
		boundErrorIsAnError ? { boundError : boundError } : { boundEvent : boundError }
	);
	const error = (category === 'user') ? user(msg, details) : library(msg, details);
	all.splice(all.indexOf(error), 1);
	return (error);
};

export function bindFromUnknown(
	message : string,
	data : Partial<IErrorDetails>,
	boundError : unknown
)
{
	if (isExtendedError(boundError)) {
		return bind(message, data, boundError);
	} else if (boundError instanceof Event) {
		return bind(message, data, boundError);
	} else if (boundError instanceof Error) {
		return bind(message, data, boundError);
	}
	const isUserError = data.isUserError === true;
	return (bind(message, data, createError(Error, 'Unknown error!', {
		bound : boundError,
	}, {
		isLibraryError : !isUserError,
		isUserError
	})));
};


export function clear() {
	all.length = 0;
	last = null;
};

export async function display(err : IErrorExtended) {
	const infos = (
		(await summarize(err)).map(x => `  ${ x.type }: ${ x.name }: ${ x.message }`)
	);
	console.error(infos.join('\n'));
	console.error(err.stack);
};

export function isExtendedError(error : any) : error is IErrorExtended {
	return error && typeof error === 'object' && 'details' in error && 'isExtendedError' in error.details;
};

export function handle<T extends void | Promise<void>>(
	error : unknown | null,
	_switch : {
		success? : () => T;
		userError? : (error : IErrorExtended) => T;
		libraryError? : (error : IErrorExtended) => T;
		unknownError? : (error : unknown) => T;
		unhandledError? : (error : unknown) => T;
	}
) : T | null
{
	if (!error) {
		if (_switch.success) {
			return _switch.success();
		}
	} else {
		if (isExtendedError(error)) {
			if (error.details.isUserError) {
				if (_switch.userError) {
					return _switch.userError(error);
				}
			} else if (error.details.isLibraryError) {
				if (_switch.libraryError) {
					return _switch.libraryError(error);
				}
			}
		} else if (_switch.unknownError) {
			return _switch.unknownError(error);
		}
		if (_switch.unhandledError) {
			return _switch.unhandledError(error);
		}
	}
	return null;
};

export async function summarize(error : IErrorExtended) {
	var out = [];
	let currentError : Error | IErrorExtended | null = error;
	while (currentError) {
		out.push(await createInfo(currentError));
		currentError = isExtendedError(currentError) ? currentError.details.boundError || null : null;
	}
	if (out.length === 1) {
		return (out);
	}
	return (out.reverse());
};

export async function summarizeLastError() {
	const error = getLast();
	if (!error) {
		throw user('There no error to summarize!');
	}
	return (await summarize(error));
};

/*bad function
export function getBoundAt(baseError : IErrorExtended, baseIdx : number) {
	let error : IErrorExtended | null = baseError;
	let idx = baseIdx;
	while (idx-- !== 0) {
		if (
			!error.details ||
			!error.details.boundError
		)
		{
			throw user(
				(
					`Trying to get a bound error at index<${ idx } ` +
					`from error<${ baseError }> but there no error at this path!`
				),
				{
					baseError,
					baseIdx
				}
			);
		}
		error = error.details.boundError;
	}
	return (error);
};*/

export function addError(error : IErrorExtended) {
	all.push(error);
	last = error;
};

export const getFromName = (() => {
	const errors = Object.freeze(
		[
			Error,
			EvalError,
			RangeError,
			ReferenceError,
			SyntaxError,
			TypeError
		].reduce((a, c) => {
			a[ c.name ] = c;
			return (a);
		}, <Record<string, unknown>> {})
	);
	return ((name : string) => errors[ name ]);
})();

export function getLast() {
	return (last);
};

export function getAll() {
	return (all);
};

export function createUnknownError(from : unknown) {
	if (from instanceof Error) {
		return library('Unknown error', { boundError : from });
	} else {
		if (typeof from === 'string') {
			return library(`Unknown error: ${ from }!`);
		}
		return library('Unknown error', { bound : from });
	}
};

export function removeHandledError(error : IErrorExtended) {
	all.splice(all.indexOf(error), 1);
	if (last === error) {
		last = all[ all.length - 1 ] || null;
	}
};

export function userAssert(condition : any, ...args : Parameters<typeof user>) : asserts condition {
	if (!condition) {
		throw user(...args);
	}
};

export function libraryAssert(condition : any, ...args : Parameters<typeof library>) : asserts condition {
	if (!condition) {
		throw library(...args);
	}
};
